import { FC, MouseEvent, useState } from 'react';
import { Avatar, Box, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { IconMap, Props } from './helpers';

const useStyles = createUseStyles((theme: Theme) => ({
  icon: {
    '& svg': {
      width: 20,
      height: 20,
      color: theme.palette.text.primary,
    },
    '&.small svg': {
      width: 16,
      height: 16,
    },
    '&.x-small svg': {
      width: 12,
      height: 12,
    },
    '&.large svg': {
      width: 24,
      height: 24,
    },
    '&.x-large svg': {
      width: 32,
      height: 32,
    },
    '& span': {
      lineHeight: '13px', // @atlaskit/icon sizing compatibility
    },
    '&.small span, &.x-small span': {
      lineHeight: '15px', // @atlaskit/icon sizing compatibility
    },
    '&.button': {
      cursor: 'pointer',
      '&:hover:not(.disabled)': {
        opacity: 0.9,
      },
    },
    '&.white svg': {
      color: theme.palette.common.white,
    },
    '&.black svg': {
      color: theme.palette.common.black,
    },
    '&.grey svg': {
      color: theme.palette.grey[400],
    },
    '&.active svg': {
      color: theme.palette.secondary.main,
    },
    '&.success svg': {
      color: theme.palette.success.main,
    },
    '&.error svg': {
      color: theme.palette.error.main,
    },
    '&.link svg': {
      color: theme.palette.link.main,
    },
    '&.primary svg': {
      color: theme.palette.primary.main,
    },
    '&.warning svg': {
      color: theme.palette.warning.main,
    },
    '&.disabled': {
      cursor: 'default',
      opacity: 0.2,
    },
  },
  iconBackground: {
    backgroundColor: theme.palette.secondary.light,
    '&.second': {
      backgroundColor: theme.palette.secondary.dark,
    },
    '&.small': {
      width: 28,
      height: 28,
    },
    '&.x-small': {
      width: 22,
      height: 22,
    },
  },
  iconWrapper: {
    lineHeight: '99%',
  },
  spin: {
    animation: '$spin 400ms linear',
  },
  pop: {
    '& svg': {
      animation: '$pop 200ms ease-out',
    },
  },
  '@keyframes spin': {
    from: {
      transform: 'rotate(0deg)',
    },
    to: {
      transform: 'rotate(360deg)',
    },
  },
  '@keyframes pop': {
    '0%': {
      transform: 'scale(1)',
    },
    '30%': {
      transform: 'scale(1.3)',
    },
    '100%': {
      transform: 'scale(1)',
    },
  },
}));

const Icon: FC<Props> = ({
  color = '',
  name,
  onClick,
  spinOnClick = false,
  pop = false,
  background,
  className = '',
  size = 'medium',
  disabled = false,
  testId,
}) => {
  const styles = useStyles();
  const Icon = IconMap.get(name);
  const [spin, setSpin] = useState(false);

  if (!Icon) {
    console.warn(`unknown icon type ${name}`);
    return null;
  }

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }
    onClick?.(event);
    spinOnClick && setSpin(true);
  };

  const handleStopSpin = () => {
    setSpin(false);
  };

  const hasOnClick = !!onClick;
  const role = hasOnClick ? 'button' : 'presentation';
  const hasBackground = !!background;

  const IconEl = <Icon label="" />;

  const wrapperClass = classNames(
    styles.icon,
    hasBackground ? styles.iconBackground : styles.iconWrapper,
    spin && styles.spin,
    pop && styles.pop,
    className,
    background,
    color,
    size,
    { button: hasOnClick, disabled, background: hasBackground }
  );

  if (hasBackground) {
    return (
      <Avatar className={wrapperClass} role={role} onClick={handleClick} data-testid={testId}>
        {IconEl}
      </Avatar>
    );
  }

  return (
    <Box
      onAnimationEnd={handleStopSpin}
      className={wrapperClass}
      role={role}
      onClick={handleClick}
      data-testid={testId}
    >
      {IconEl}
    </Box>
  );
};

export default Icon;
